import { Box } from "@mui/material";
import {
  createFileRoute,
  redirect,
  Outlet,
  useMatches,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { UploadProgressOverlay } from "@/features/AssetUpload/components/UploadProgressOverlay";
import { uploadManager } from "@/features/AssetUpload/uploadManager";
import { EmailSetupBanner } from "@/features/EmailSetup/EmailSetupBanner";
import { ExportBanner } from "@/features/Exports/ExportBanner";
import { AppMaintenanceWindow } from "@/features/Maintenance/AppMaintenanceWindow";
import { useIsAppAccessBlockedByMaintenanceMode } from "@/features/Maintenance/flag";
import { MaintenanceBanner } from "@/features/Maintenance/MaintenanceBanner";
import { Navbar } from "@/features/Navigation/Navbar";
import { TestModeBanner } from "@/features/TestMode/TestModeBanner";
import { useConfiguredSiteRedirect } from "@/hooks/useConfiguredSiteRedirect";
import { redirectDemoUserOffLimits, useIsDemoUser } from "@/utils/demoMode";

const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "development";

export const Route = createFileRoute("/_auth")({
  beforeLoad: ({ context, location }) => {
    // Demo accounts: allowlisted routes only (runs in dev too, FOG-2234).
    redirectDemoUserOffLimits(context.auth?.user?.roles, location.pathname);

    // Skip auth check in development mode
    if (isDevelopment) {
      return;
    }

    if (!context.auth?.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
  },
  component: AuthLayout,
});

export function AuthLayout() {
  useConfiguredSiteRedirect();
  const isDemoUser = useIsDemoUser();
  const matches = useMatches();
  const isBlockedByMaintenance = useIsAppAccessBlockedByMaintenanceMode();
  const isStandalonePage = matches.some(
    (m) =>
      m.routeId?.startsWith("/_auth/brand-onboarding/") ||
      m.routeId?.startsWith("/_auth/apple-messages/"),
  );

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (uploadManager.hasActiveUploads()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  if (isBlockedByMaintenance) {
    return <AppMaintenanceWindow />;
  }

  return (
    <Box
      className="auth-shell"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {isStandalonePage ? (
        <Outlet />
      ) : (
        <>
          {/* Setup/test-mode banners point at Settings, which demo users can't open. */}
          {!isDemoUser && <EmailSetupBanner />}
          <MaintenanceBanner />
          {!isDemoUser && <TestModeBanner />}
          <ExportBanner />
          <Navbar />
          <Box
            className="auth-scroll-area"
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              position: "relative",
            }}
          >
            <Outlet />
          </Box>
          {/* AuthLayout is the authenticated-app shell (hosts the Navbar). Mount the overlay here so it persists across all authenticated routes. */}
          <UploadProgressOverlay />
        </>
      )}
    </Box>
  );
}
